import React, { useEffect, useCallback, useRef, useState } from "react";
import moment from "moment";
import { useApp } from "../../../../context/AppContext";
import { useTransaction } from "../../../../context/TransactionContext";
import { notifyServiceBookingError } from "../../../../utils/serviceAvailabilityError";
import { notifyError } from "../../../../utils/notify";
import {
  transformTransactionResponseToGetTransactionsResponse,
  transformTransactionToEvent,
} from "../../../../utils/transactionUtils";
import {
  CalendarEventType,
  SlotInfo,
  TimeBlockEvent,
} from "../../../../types/calendar";
import { mergeUpdatedTimeBlocks } from "../calendarEventUtils";
import { useTypedCalendarEvent } from "../calendarViewTypes";
import type { DrawerSlotApi } from "../drawer/drawerSlot";

/** New time block prefilled from a pressed slot ("HH:mm" start + staff). */
export interface TimeBlockSeed {
  startTime: string;
  resourceId: string;
  /** The block's day (mobile week view press); default the viewed day. */
  date?: Date;
}

interface UseSchedulingActionsArgs {
  businessId: string | undefined;
  timezone: string | undefined;
  salonServices: any[];
  multiLocationOn: boolean;
  selectedDate: Date;
  refreshServices: () => Promise<unknown>;
  mapTimeBlocks: (timeBlocks: any[], staffId?: string) => TimeBlockEvent[];
  refetchMonthBuffer: (date: Date) => Promise<void>;
  /** The drawer slot: the time block drawer holds it as "timeBlock". */
  drawerSlot?: Pick<DrawerSlotApi, "open" | "close" | "setEvictor">;
}

/**
 * The FAB / empty-slot create flows: the empty-slot chooser (desktop) and the
 * mobile slot menu's seeded openers, the legacy appointment modal (mobile
 * create), the time block create/edit/delete modal, and Quick Sale. Owns their open state and the
 * shared submit `isLoading` flag. Desktop appointment booking is the booking
 * drawer (booking/useBookingSession).
 */
export function useSchedulingActions({
  businessId,
  timezone,
  salonServices,
  multiLocationOn,
  selectedDate,
  refreshServices,
  mapTimeBlocks,
  refetchMonthBuffer,
  drawerSlot,
}: UseSchedulingActionsArgs) {
  const {
    createAppointment,
    createTimeBlock,
    updateTimeBlock,
    deleteTimeBlock,
  } = useApp();
  const { fetchTransaction, addTransaction } = useTransaction();
  const { selectedEvent, setSelectedEvent, setSelectedAction, setEvents } =
    useTypedCalendarEvent();

  const [isLoading, setIsLoading] = useState(false); // For appointment/timeblock operations
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isTimeBlockModalOpen, setTimeBlockOpenState] = useState(false);
  // The time block surface's open state is the drawer slot's "timeBlock"
  // (opening it closes the booking / appointment drawer).
  const slotOpen = drawerSlot?.open;
  const slotClose = drawerSlot?.close;
  const setIsTimeBlockModalOpen = useCallback(
    (open: boolean) => {
      if (open) slotOpen?.("timeBlock");
      else slotClose?.("timeBlock");
      setTimeBlockOpenState(open);
    },
    [slotOpen, slotClose],
  );
  // Another drawer took the slot: close the time block drawer as is.
  const setEvictor = drawerSlot?.setEvictor;
  useEffect(() => {
    setEvictor?.("timeBlock", () => {
      setTimeBlockOpenState(false);
      setSelectedEvent(null);
      setTimeBlockSeed(null);
    });
  }, [setEvictor, setSelectedEvent]);
  const [isQuickSaleModalOpen, setIsQuickSaleModalOpen] = useState(false);
  // Slot selection state (for creating appointments)
  const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
  // Empty-slot chooser (Appointment / Time block), anchored at the click.
  // null = closed.
  const [slotChooserAt, setSlotChooserAt] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Time-block modal seed for a new block created from a pressed slot on
  // mobile (start time + staff). null = no seed (FAB / desktop chooser).
  const [timeBlockSeed, setTimeBlockSeed] = useState<TimeBlockSeed | null>(
    null,
  );
  // Last pointer position over the calendar. RBC's slotInfo box/bounds are
  // inconsistent across day/week views (a click may carry no clientX), so the
  // view area records the real pointer to anchor the chooser.
  const lastPointerRef = useRef<{ x: number; y: number }>({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
  const capturePointer = useCallback((e: React.PointerEvent) => {
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  // Open the time-block modal on an existing block.
  const openTimeBlockEditor = useCallback(
    (event: CalendarEventType) => {
      setSelectedEvent(event);
      setTimeBlockSeed(null);
      setIsTimeBlockModalOpen(true);
    },
    [setIsTimeBlockModalOpen, setSelectedEvent],
  );

  // Empty-slot click: show the Appointment / Time block chooser at the click.
  const handleSlotSelect = useCallback(
    (slotInfo: SlotInfo) => {
      setSelectedSlot(slotInfo);
      setSelectedEvent(null);
      setSlotChooserAt({ ...lastPointerRef.current });
    },
    [setSelectedEvent],
  );

  const dismissSlotChooser = useCallback(() => {
    setSlotChooserAt(null);
    setSelectedSlot(null);
  }, []);

  // Chooser → Appointment on mobile: the legacy modal seeded from the slot.
  const openAppointmentModalForSlot = useCallback(() => {
    setSlotChooserAt(null);
    setIsAppointmentModalOpen(true);
  }, []);

  // Chooser → Appointment on desktop: the booking drawer takes over.
  const closeSlotChooserForBooking = useCallback(() => {
    setSlotChooserAt(null);
    setSelectedSlot(null);
  }, []);

  // Chooser → Time block.
  const openTimeBlockForSlot = useCallback(() => {
    setSlotChooserAt(null);
    // Seed the new block from the clicked slot (start + staff column).
    setTimeBlockSeed(
      selectedSlot
        ? {
            startTime: `${String(selectedSlot.start.getHours()).padStart(2, "0")}:${String(selectedSlot.start.getMinutes()).padStart(2, "0")}`,
            resourceId: (selectedSlot.resourceId as string) || "",
            date: selectedSlot.start,
          }
        : null,
    );
    setSelectedSlot(null);
    setSelectedAction("block");
    setIsTimeBlockModalOpen(true);
  }, [setSelectedAction, selectedSlot, setIsTimeBlockModalOpen]);

  // Mobile slot menu → Appointment: the legacy create modal seeded with the
  // pressed slot (start + staff column).
  const openAppointmentModalAt = useCallback(
    (slot: SlotInfo) => {
      setSelectedEvent(null);
      setSelectedSlot(slot);
      setIsAppointmentModalOpen(true);
    },
    [setSelectedEvent],
  );

  // Mobile slot menu → Time block: the time-block modal seeded with the
  // pressed start time + staff column.
  const openTimeBlockAt = useCallback(
    (seed: TimeBlockSeed) => {
      setSelectedAction("block");
      setSelectedEvent(null);
      setTimeBlockSeed(seed);
      setIsTimeBlockModalOpen(true);
    },
    [setIsTimeBlockModalOpen, setSelectedAction, setSelectedEvent],
  );

  const handleNewAppointment = useCallback(() => {
    setSelectedEvent(null);
    setSelectedSlot(null);
    setIsAppointmentModalOpen(true);
  }, [setSelectedEvent]);

  const handleNewTimeBlock = useCallback(() => {
    setSelectedAction("block");
    setSelectedEvent(null);
    setTimeBlockSeed(null);
    setIsTimeBlockModalOpen(true);
  }, [setIsTimeBlockModalOpen, setSelectedAction, setSelectedEvent]);

  const handleNewQuickSale = useCallback(() => {
    setIsQuickSaleModalOpen(true);
  }, []);

  const closeQuickSaleModal = useCallback(() => {
    setIsQuickSaleModalOpen(false);
  }, []);

  const closeAppointmentModal = useCallback(() => {
    setIsAppointmentModalOpen(false);
    setSelectedEvent(null);
    setSelectedSlot(null);
  }, [setSelectedEvent]);

  const closeTimeBlockModal = useCallback(() => {
    setIsTimeBlockModalOpen(false);
    setSelectedEvent(null);
    setTimeBlockSeed(null);
  }, [setIsTimeBlockModalOpen, setSelectedEvent]);

  const handleQuickSaleComplete = useCallback(
    async (transactionId: string) => {
      // Fetch the single transaction and add it to the list
      try {
        const transactionResponse = await fetchTransaction(transactionId);

        if (transactionResponse) {
          const transactionData =
            transformTransactionResponseToGetTransactionsResponse(
              transactionResponse,
            );

          addTransaction(transactionData);

          // Quick sales don't have appointments, so they won't show on the
          // calendar; transform anyway in case of appointment-like quick sales.
          const newEvents = transformTransactionToEvent(
            transactionData,
            timezone || "",
            salonServices,
          );

          if (newEvents.length > 0) {
            setEvents(
              (prevEvents) =>
                [...prevEvents, ...newEvents] as CalendarEventType[],
            );
          }
        }
      } catch (error) {
        console.error("Error fetching quick sale transaction:", error);
      }
    },
    [fetchTransaction, addTransaction, timezone, salonServices, setEvents],
  );

  // Legacy appointment modal submit (create only: mobile).
  const handleAppointmentSubmit = useCallback(
    async (appointmentData: any) => {
      setIsLoading(true);
      try {
        const response = await createAppointment(appointmentData);
        if (response) {
          const newEvents = transformTransactionToEvent(
            response,
            timezone || "",
            salonServices,
          );
          const newTransaction =
            transformTransactionResponseToGetTransactionsResponse(response);
          addTransaction(newTransaction);
          setEvents(
            (prevEvents) =>
              [...prevEvents, ...newEvents] as CalendarEventType[],
          );
          // The new event is dropped onto the calendar; we intentionally do
          // NOT auto-open its detail modal. Operators open an appointment on
          // demand (single-click preview, or double-click for the modal).
        }

        setIsAppointmentModalOpen(false);
        setSelectedSlot(null);
      } catch (error) {
        console.error("Error with appointment:", error);
        notifyServiceBookingError(error, { refreshServices });
      } finally {
        setIsLoading(false);
      }
    },
    [
      createAppointment,
      timezone,
      salonServices,
      addTransaction,
      setEvents,
      refreshServices,
    ],
  );

  // Handle time block submit
  const handleTimeBlockSubmit = useCallback(
    async (blockData: any) => {
      setIsLoading(true);
      try {
        if (selectedEvent) {
          const response = await updateTimeBlock(blockData);
          if (response) {
            const updatedEvents = mapTimeBlocks(
              response,
              selectedEvent.resourceId,
            );
            setEvents((prevEvents) =>
              mergeUpdatedTimeBlocks(prevEvents, updatedEvents),
            );
          }
        } else {
          // Multi-location (PR #356 / infra): POST /timeoff scopes the new row
          // to the SELECTED location so a multi-location staff member's time
          // off no longer bleeds across locations. The location is the
          // currently selected salon (SalonContext businessInfo.id — see
          // currentLocation.ts). Only the create path sends it; edits target an
          // existing row by id, and the GET already carries the salon in its
          // path (/business-locations/{id}/timeoff). Flag-OFF users must NOT send the field
          // at all: the current backend's create_time_off rejects unexpected
          // fields with a 400, so attaching it unconditionally would break
          // time-off creation for every user until the backend deploy lands.
          // Gated on multiLocationOn (available_locations from /access), which
          // is only non-empty on backends that accept it.
          const response = await createTimeBlock(
            multiLocationOn
              ? { ...blockData, business_location_uuid: businessId }
              : blockData,
          );
          const newEvents = mapTimeBlocks(response);

          if (blockData.recurrence_interval) {
            // For recurring time blocks, fetch the current buffer to get all instances
            await refetchMonthBuffer(selectedDate);
          } else {
            setEvents(
              (prevEvents) =>
                [...prevEvents, ...newEvents] as CalendarEventType[],
            );
          }
        }

        setIsTimeBlockModalOpen(false);
        setSelectedEvent(null);
      } catch (error: any) {
        console.error("Error with time block:", error);
        // The backend's 4xx message (e.g. "recurrence_end_date must be after
        // end_dt") for anything the drawer's validation didn't catch.
        notifyError(
          error?.errorBody?.message ??
            "Could not save the time block. Please try again.",
          { title: "Time block not saved" },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsTimeBlockModalOpen,
      selectedEvent,
      updateTimeBlock,
      mapTimeBlocks,
      setEvents,
      createTimeBlock,
      multiLocationOn,
      businessId,
      refetchMonthBuffer,
      selectedDate,
      setSelectedEvent,
    ],
  );

  // Handle time block delete
  const handleTimeBlockDelete = useCallback(
    async (appliesTo: string) => {
      setIsLoading(true);
      try {
        await deleteTimeBlock(selectedEvent!.id, appliesTo);

        // Deleting the first occurrence of a series re-parents the remaining
        // occurrences server-side, so local state's parent link goes stale.
        const isSeriesParentSingleDelete =
          appliesTo === "single" &&
          "recurrenceParentId" in selectedEvent! &&
          selectedEvent!.recurrenceParentId === selectedEvent!.id;

        setEvents((prevEvents) => {
          if (appliesTo === "all" && "recurrenceParentId" in selectedEvent!) {
            return prevEvents.filter(
              (event) =>
                !("recurrenceParentId" in event) ||
                event.recurrenceParentId !== selectedEvent!.recurrenceParentId,
            );
          } else if (
            appliesTo === "hereafter" &&
            "recurrenceParentId" in selectedEvent!
          ) {
            const selectedEventStart = moment(selectedEvent!.start);
            return prevEvents.filter(
              (event) =>
                !("recurrenceParentId" in event) ||
                event.recurrenceParentId !==
                  selectedEvent!.recurrenceParentId ||
                moment(event.start).isBefore(selectedEventStart),
            );
          } else {
            return prevEvents.filter((event) => event.id !== selectedEvent!.id);
          }
        });

        // Refresh the buffer so the re-parented occurrences pick up their new
        // recurrenceParentId (the merge updates existing time blocks in place).
        if (isSeriesParentSingleDelete) {
          await refetchMonthBuffer(selectedDate);
        }

        setIsTimeBlockModalOpen(false);
        setSelectedEvent(null);
      } catch (error: any) {
        console.error("Error deleting time block:", error);
        notifyError(
          error?.errorBody?.message ??
            "Could not delete the time block. Please try again.",
          { title: "Time block not deleted" },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsTimeBlockModalOpen,
      deleteTimeBlock,
      selectedEvent,
      setEvents,
      refetchMonthBuffer,
      selectedDate,
      setSelectedEvent,
    ],
  );

  return {
    selectedEvent,
    selectedSlot,
    timeBlockSeed,
    slotChooserAt,
    isLoading,
    setIsLoading,
    isAppointmentModalOpen,
    isTimeBlockModalOpen,
    isQuickSaleModalOpen,
    openTimeBlockEditor,
    capturePointer,
    handleSlotSelect,
    dismissSlotChooser,
    openAppointmentModalForSlot,
    closeSlotChooserForBooking,
    openTimeBlockForSlot,
    openAppointmentModalAt,
    openTimeBlockAt,
    handleNewAppointment,
    handleNewTimeBlock,
    handleNewQuickSale,
    closeQuickSaleModal,
    closeAppointmentModal,
    closeTimeBlockModal,
    handleQuickSaleComplete,
    handleAppointmentSubmit,
    handleTimeBlockSubmit,
    handleTimeBlockDelete,
  };
}

export type SchedulingActions = ReturnType<typeof useSchedulingActions>;
